/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 *
 * Service for managing external access requests via the user management service API.
 * Provides methods to create, retrieve, list, update the status of, and verify external access
 * requests for a given organization.
 *
 * Features:
 * - `requestExternalAccess$()` — submits a new external access request (POST).
 * - `loadAccessRequests$()` — fetches a paginated list of access requests (GET).
 * - `getAccessRequest$()` — retrieves a single access request by ID (GET).
 * - `updateAccessRequestStatus$()` — updates the status of an access request (PUT).
 * - `verifyEmail$()` — verifies the applicant's email address via the public verification endpoint (POST).
 *   Requires `organizationId` and `requestId`; no authentication needed.
 */

import {inject, Injectable} from '@angular/core';
import {Observable} from 'rxjs';

import {environment} from '../environments/environment';
import {AccessRequestDto} from '../models/access-request.dto';
import {
  AccessRequest,
  AccessRequestListParams,
  AccessRequestStatus,
} from '../models/AccessRequest';
import {ApiClient} from './api-client.service';

/** Shape of the raw paginated list response from the user-management service. */
interface AccessRequestListResponse {
  readonly links: unknown;
  readonly data: AccessRequest[];
}

/** Shape of the raw single-item response from the user-management service. */
interface AccessRequestResponse {
  readonly data: AccessRequest;
}

@Injectable({
  providedIn: 'root',
})
export class AccessRequestManagementService {
  private readonly apiClient = inject(ApiClient);

  /**
   * Submits a new external access request for the given organization.
   *
   * Errors propagate to `GlobalErrorHandlerService` automatically (Pattern A).
   *
   * @param organizationId - The ID of the organization the request targets.
   * @param payload - The access request payload containing applicant details and desired roles.
   * @returns An `Observable` that completes when the request was created successfully.
   */
  requestExternalAccess$(
    organizationId: string,
    payload: AccessRequestDto,
  ): Observable<void> {
    return this.apiClient.post<void>(
      `${environment.backend.user_host}/organizations/${organizationId}/requests`,
      {data: payload},
    );
  }

  /**
   * Fetches a paginated list of external access requests for the given organization.
   *
   * Errors propagate to `GlobalErrorHandlerService` automatically (Pattern A).
   *
   * @param organizationId - The ID of the organization whose requests are fetched.
   * @param params - Optional pagination and filter parameters (`offset`, `limit`, `status`).
   * @returns An `Observable` emitting `{ links, data }` with the paginated access requests.
   */
  loadAccessRequests$(
    organizationId: string,
    params?: AccessRequestListParams,
  ): Observable<{links: unknown; data: AccessRequest[]}> {
    const query = new URLSearchParams();
    if (params?.offset !== undefined) {
      query.set('offset', String(params.offset));
    }
    if (params?.limit !== undefined) {
      query.set('limit', String(params.limit));
    }
    if (params?.status) {
      query.set('status', params.status);
    }
    const queryString = query.toString() ? `?${query.toString()}` : '';
    return this.apiClient.get<AccessRequestListResponse>(
      `${environment.backend.user_host}/organizations/${organizationId}/requests${queryString}`,
    );
  }

  /**
   * Fetches a single external access request by its ID.
   *
   * Errors propagate to `GlobalErrorHandlerService` automatically (Pattern A).
   *
   * @param organizationId - The ID of the organization that owns the request.
   * @param requestId - The ID of the access request to retrieve.
   * @returns An `Observable` emitting the access request wrapped in `{ data }`.
   */
  getAccessRequest$(
    organizationId: string,
    requestId: string,
  ): Observable<{data: AccessRequest}> {
    return this.apiClient.get<AccessRequestResponse>(
      `${environment.backend.user_host}/organizations/${organizationId}/requests/${requestId}`,
    );
  }

  /**
   * Updates the status of an external access request to `ACCEPTED` or `REJECTED`.
   * Only requests with status `CREATED` can be updated.
   *
   * Errors propagate to `GlobalErrorHandlerService` automatically (Pattern A).
   *
   * @param organizationId - The ID of the organization that owns the request.
   * @param requestId - The ID of the access request to update.
   * @param status - The new status: either `AccessRequestStatus.Accepted` or `AccessRequestStatus.Rejected`.
   * @param reason - Optional reason text supplied when declining a request.
   * @returns An `Observable` emitting the updated access request wrapped in `{ data }`.
   */
  updateAccessRequestStatus$(
    organizationId: string,
    requestId: string,
    status: AccessRequestStatus,
    reason?: string,
  ): Observable<{data: AccessRequest}> {
    return this.apiClient.put<AccessRequestResponse>(
      `${environment.backend.user_host}/organizations/${organizationId}/requests/${requestId}/status`,
      {data: {status, ...(reason ? {reason} : {})}},
    );
  }

  /**
   * Verifies the applicant's email address by calling the public email verification endpoint.
   *
   * This method does **not** require authentication — the backend route is publicly accessible.
   * Errors propagate to `GlobalErrorHandlerService` automatically (Pattern A).
   *
   * @param organizationId - The ID of the organization the access request belongs to.
   * @param requestId - The UUID of the access request, used as the single-use verification token.
   * @returns An `Observable` that completes with HTTP 204 when the email was verified successfully.
   */
  verifyEmail$(organizationId: string, requestId: string): Observable<void> {
    return this.apiClient.post<void>(
      `${environment.backend.user_host}/organizations/${organizationId}/requests/${requestId}/verify`,
      null,
    );
  }
}
